import { useEffect, useState } from 'react';
import { DirectusService } from '@/services/directusService';

/**
 * Resolve a `pages` collection slug (e.g. "loja", "contactos") to its real
 * integer primary key.
 *
 * The Directus `pages` collection uses an auto-increment integer PK, but the UI
 * references pages by slug. The Visual Editor needs the actual PK both to fetch
 * the item and to open the correct Studio drawer, so passing the slug directly
 * as `itemId` yields a 403 (`/items/pages/<slug>` is not a valid PK). This hook
 * looks the page up by slug once (via {@link DirectusService.getPage}) and
 * returns its numeric id, or `null` until resolved / if the page doesn't exist.
 *
 * Results are cached process-wide so multiple editors on the same page share a
 * single lookup.
 */
const idCache = new Map<string, number | null>();
const inflight = new Map<string, Promise<number | null>>();

async function resolvePageId(slug: string): Promise<number | null> {
  if (idCache.has(slug)) return idCache.get(slug) ?? null;

  let request = inflight.get(slug);
  if (!request) {
    request = DirectusService.getPage(slug)
      .then((page) => {
        const id = page && typeof page.id === 'number' ? page.id : null;
        idCache.set(slug, id);
        return id;
      })
      .catch(() => {
        // Missing page / permission error — cache the miss so we don't refetch.
        idCache.set(slug, null);
        return null;
      })
      .finally(() => {
        inflight.delete(slug);
      });
    inflight.set(slug, request);
  }
  return request;
}

export function usePageId(slug: string): number | null {
  const [id, setId] = useState<number | null>(() => idCache.get(slug) ?? null);

  useEffect(() => {
    let active = true;
    resolvePageId(slug).then((resolved) => {
      if (active) setId(resolved);
    });
    return () => {
      active = false;
    };
  }, [slug]);

  return id;
}

export default usePageId;
